import { useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { getNextPhaseChangeAt } from "./statusCountdown";

// ---------------------------------------------------------------------------
// Ticker compartido: un unico setInterval global para todos los contadores.
// ---------------------------------------------------------------------------
const TICK_MS = 1000;
// setTimeout no soporta retardos mayores a 2^31 - 1 ms
const MAX_TIMEOUT_MS = 2147483647;

const listeners = new Set();
let intervalId = null;

const notifyAll = () => {
  const now = Date.now();
  // React 17 no agrupa setState fuera de eventos de React: se agrupan aqui
  // para generar un solo commit por segundo sin importar cuantos contadores haya.
  unstable_batchedUpdates(() => {
    listeners.forEach((listener) => listener(now));
  });
};

const handleVisibilityChange = () => {
  // Al volver a la pestana se actualiza de inmediato (los timers pudieron frenarse)
  if (!document.hidden) notifyAll();
};

const startTicker = () => {
  if (intervalId !== null) return;
  intervalId = setInterval(() => {
    if (document.hidden) return; // sin nadie mirando no se hace trabajo
    notifyAll();
  }, TICK_MS);
  document.addEventListener("visibilitychange", handleVisibilityChange);
};

const stopTicker = () => {
  if (intervalId === null) return;
  clearInterval(intervalId);
  intervalId = null;
  document.removeEventListener("visibilitychange", handleVisibilityChange);
};

const subscribe = (listener) => {
  listeners.add(listener);
  startTicker();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stopTicker();
  };
};

/**
 * Devuelve el timestamp actual, actualizado cada segundo.
 * Si `enabled` es false no se suscribe al ticker (costo cero).
 */
export const useNow = (enabled = true) => {
  const [now, setNow] = useState(() => Date.now());
  const hasRunRef = useRef(false);

  useEffect(() => {
    const isFirstRun = !hasRunRef.current;
    hasRunRef.current = true;
    if (!enabled) return undefined;
    // En el montaje el estado inicial ya es Date.now(): no hace falta otro render.
    // Si `enabled` se activa despues (ej. llegan los parametros), se resincroniza.
    if (!isFirstRun) setNow(Date.now());
    return subscribe(setNow);
  }, [enabled]);

  return now;
};

/**
 * Fuerza un re-render unicamente cuando alguno de los casos cruza un umbral
 * (tiempo limite o tiempo critico). Programa un solo setTimeout hacia el
 * proximo cambio de fase: no hay re-renders periodicos.
 *
 * Sirve para que los colores calculados con `paintCard` se actualicen en vivo.
 */
export const usePhaseChangeRefresh = (cases, timeParamsByStatus) => {
  const [tick, setTick] = useState(0);
  const nextChangeAt = getNextPhaseChangeAt(cases, timeParamsByStatus);

  useEffect(() => {
    if (nextChangeAt === null) return undefined;
    const delay = Math.min(
      Math.max(nextChangeAt - Date.now(), 0) + 50,
      MAX_TIMEOUT_MS
    );
    const timeoutId = setTimeout(() => setTick((t) => t + 1), delay);
    return () => clearTimeout(timeoutId);
  }, [nextChangeAt, tick]);
};
